import React, { useState, useRef } from "react";
import "./App.css";

function App() {
  // Detect browser language on first load
  const getDefaultLang = () => {
    const navLang = navigator.language || navigator.languages?.[0] || "en";
    const isNorwegian =
      navLang.startsWith("no") ||
      navLang.startsWith("nb") ||
      navLang.startsWith("nn");
    return isNorwegian ? "nb" : "en";
  };

  const [lang, setLang] = useState<"nb" | "en">(getDefaultLang());

  const texts = {
    nb: {
      name: "Oliver Lysø",
      about1:
        "Jeg er lidenskapelig opptatt av teknologi og hvordan det kan brukes til å løse komplekse utfordringer og skape nye muligheter. Målet mitt er å bruke min tekniske kompetanse til å utvikle innovative løsninger som ikke bare dekker dagens behov, men også framtidens behov. Jeg har sterk tro på at teknologi kan være en kraft for positiv og bærekraftig forandring.",
      about2:
        "Jeg har erfaring med Power Platform, spesielt Power Apps, Dataverse og Power Automate og jeg har brukt disse verktøyene til å effektivisere og automatisere arbeidsprosesser, samt forenkle arbeidshverdagen. Ved å kombinere teknologi med problemløsning har jeg vært med på å utvikle løsninger som gir sterke resultater.",
      linkedin: "Kontakt meg", // changed text
    },
    en: {
      name: "Oliver Lysø",
      about1:
        "I am passionate about technology and how it can be used to solve complex challenges and create new opportunities. My goal is to use my technical skills to develop innovative solutions that meet not only today's needs, but also the needs of the future. I firmly believe that technology can be a force for positive and sustainable change.",
      about2:
        "I have experience with the Power Platform, especially Power Apps, Dataverse, and Power Automate, and I have used these tools to streamline and automate work processes, as well as simplify the everyday workflow. By combining technology with problem-solving, I have contributed to developing solutions that deliver strong results.",
      linkedin: "Contact me", // changed text
    },
  };

  const btnRef = useRef<HTMLAnchorElement>(null);

  const handleBtnMouseMove = (e: React.MouseEvent) => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
    btn.style.transform = `translate(${x}px, ${y}px)`;
  };

  const handleBtnMouseLeave = () => {
    const btn = btnRef.current;
    if (btn) btn.style.transform = "translate(0,0)";
  };

  const iconInfo = [
    {
      key: "powerapps",
      img: "/microsoft-power-apps-2020.png",
      alt: "Power Apps",
      text: {
        nb: "Power Apps er ett verktøy i Microsoft Power Platform som jeg har brukt til å lage skreddersydde forretningsapplikasjoner som strømlinjeformer arbeidsprosesser og sparer tid.",
        en: "Power Apps is a tool in the Microsoft Power Platform that I have used to create custom business applications that streamline work processes and save time.",
      },
    },
    {
      key: "dataverse",
      img: "/dataverse_1600x1600.png",
      alt: "Dataverse",
      text: {
        nb: "Dataverser er ett vertkøy i Microsoft Power Platform for sikker lagring og håndtering av data som brukes av forretningsapplikasjoner.",
        en: "Dataverse is a tool in the Microsoft Power Platform for secure storage and management of data used by business applications.",
      },
    },
    {
      key: "powerautomate",
      img: "/Microsoft_Power_Automate.svg.png",
      alt: "Power Automate",
      text: {
        nb: "Powerer ett vertkøy i Microsoft Power Platform som jeg har brukt til å automatisere arbeidsflyter mellom apper og tjenester for å effektivisere arbeidsprosesser.",
        en: "Power Automate is a tool in the Microsoft Power Platform that I have used to automate workflows between apps and services to streamline business processes.",
      },
    },
    {
      key: "powerbi",
      img: "/New_Power_BI_Logo.svg.png",
      alt: "Power BI",
      text: {
        nb: "Power BI er et verktøy i Microsoft Power Platform som jeg har brukt til å visualisere data og lage rapporter som gir bedre grunnlag for beslutningstaking.",
        en: "Power BI is a tool in the Microsoft Power Platform that I have used to visualize data and create reports that provide a better basis for decision-making.",
      },
    },
    {
      key: "react",
      img: "/React_(web_framework)-Logo.wine.png",
      alt: "React",
      text: {
        nb: "React er et JavaScript-bibliotek jeg har brukt til å bygge raske og interaktive apper.",
        en: "React is a JavaScript library that I have used to build fast and interactive apps.",
      },
    },
  ];

  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);

  return (
    <div className="App">
      {/* Language Selector */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "1rem 2rem 0 2rem",
        }}
      >
        <select
          value={lang}
          onChange={(e) => setLang(e.target.value as "nb" | "en")}
          style={{
            fontSize: "1rem",
            borderRadius: "8px",
            padding: "0.3rem 1rem",
            border: "none",
            background: "#fff",
            color: "#222",
            cursor: "pointer",
            outline: "none",
            boxShadow: "none",
          }}
          aria-label="Velg språk / Select language"
        >
          <option value="nb">Norsk</option>
          <option value="en">English</option>
        </select>
      </div>

      {/* About Me Section */}
      <section
        className="about-me"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <div
          style={{
            maxWidth: "1000px",
            width: "100%",
            display: "flex",
            alignItems: "flex-end", // align everything to the bottom
            gap: "2rem",
          }}
        >
          <div style={{ flex: 1, textAlign: "left" }}>
            <h2 className="gradient-text">{texts[lang].name}</h2>
            <p>{texts[lang].about1}</p>
            <p>{texts[lang].about2}</p>
            <div
              style={{
                marginTop: "2.5rem",
                display: "flex",
                gap: "1rem",
                alignItems: "flex-end", // align buttons to the bottom
                minHeight: 48, // ensures enough height for both
              }}
            >
              <a
                ref={btnRef}
                className="contact-btn"
                href="mailto:kontakt@oliverlyso.no"
                style={{ textDecoration: "none" }}
                onMouseMove={handleBtnMouseMove}
                onMouseLeave={handleBtnMouseLeave}
              >
                <span>{texts[lang].linkedin}</span>
              </a>
              <a
                href="https://www.linkedin.com/in/oliver-lyso/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#a739b1",
                  fontWeight: 500,
                  fontSize: "1.08rem",
                  textDecoration: "none",
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                  padding: 0,
                  display: "inline-block",
                  lineHeight: "48px", // match button height
                  verticalAlign: "bottom",
                  height: 48,
                }}
              >
                {lang === "nb" ? "Se LinkedIn" : "See LinkedIn"}
              </a>
            </div>
          </div>
          <img
            src="/DSC03000.png"
            alt="Oliver Lysø"
            style={{
              width: "400px",
              flexShrink: 0,
              filter: "grayscale(100%)",
              alignSelf: "center", // <-- this keeps the image vertically centered
            }}
          />
        </div>
      </section>
      {/* Technologies Gallery Section */}
      <section
        className="tech-gallery"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "2rem 0",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "2rem",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {iconInfo.map((icon) => (
            <img
              key={icon.key}
              src={icon.img}
              alt={icon.alt}
              className={
                "tech-logo" + (selectedIcon === icon.key ? " selected" : "")
              }
              style={{
                objectFit: icon.key === "react" ? "cover" : "contain",
              }}
              onClick={() =>
                setSelectedIcon(selectedIcon === icon.key ? null : icon.key)
              }
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setSelectedIcon(selectedIcon === icon.key ? null : icon.key);
                }
              }}
              aria-label={icon.alt}
            />
          ))}
        </div>
        <div
          style={{
            minHeight: selectedIcon ? "2.5rem" : 0,
            width: "100%",
            marginTop: selectedIcon ? "1.5rem" : 0,
            display: selectedIcon ? "flex" : "none",
            justifyContent: "center",
            transition: "all 0.3s",
          }}
        >
          {iconInfo.map(
            (icon) =>
              selectedIcon === icon.key && (
                <span key={icon.key} className="icon-fadein-text">
                  {icon.text[lang]}
                </span>
              )
          )}
        </div>
      </section>

      {/* Experience Timeline Section */}
      <section className="experience-section">
        <h2
          style={{
            textAlign: "center",
            marginBottom: "2.5rem",
            fontSize: "2rem",
            fontWeight: 700,
            background: "linear-gradient(90deg, #5389fd 0%, #a739b1 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            color: "transparent",
            letterSpacing: "0.02em",
          }}
        >
          {lang === "nb" ? "Erfaring & Utdanning" : "Experience & Education"}
        </h2>
        <div className="timeline">
          {/* Lærling løsningsarkitekt */}
          <div className="timeline-item">
            <div className="timeline-dot" />
            <div className="timeline-card">
              <div className="timeline-title">
                {lang === "nb"
                  ? "Lærling løsningsarkitekt"
                  : "Apprentice Solution Architect"}
              </div>
              <div className="timeline-org">SpareBank 1 Nordmøre</div>
              <div className="timeline-date">
                {lang === "nb" ? "aug. 2023 – Nå" : "Aug 2023 – Present"}
              </div>
              <div className="timeline-location">
                {lang === "nb"
                  ? "Kristiansund, Møre og Romsdal, Norge · På kontoret"
                  : "Kristiansund, Møre og Romsdal, Norway · On-site"}
              </div>
            </div>
          </div>
          {/* Praktikant */}
          <div className="timeline-item">
            <div className="timeline-dot alt" />
            <div className="timeline-card">
              <div className="timeline-title">
                {lang === "nb" ? "Praktikant" : "Intern"}
              </div>
              <div className="timeline-org">SpareBank 1 Nordmøre</div>
              <div className="timeline-date">
                {lang === "nb"
                  ? "sep. 2022 – jun. 2023"
                  : "Sep 2022 – Jun 2023"}
              </div>
              <div className="timeline-location">
                {lang === "nb"
                  ? "Kristiansund, Møre og Romsdal, Norge · På kontoret"
                  : "Kristiansund, Møre og Romsdal, Norway · On-site"}
              </div>
            </div>
          </div>
          {/* Informasjonsteknologi Vg2 */}
          <div className="timeline-item">
            <div className="timeline-dot" />
            <div className="timeline-card">
              <div className="timeline-title">
                {lang === "nb"
                  ? "Informasjonsteknologi Vg2"
                  : "Information Technology Vg2"}
              </div>
              <div className="timeline-org">
                Kristiansund Videregående Skole
              </div>
              <div className="timeline-date">
                {lang === "nb"
                  ? "aug. 2022 – jun. 2023"
                  : "Aug 2022 – Jun 2023"}
              </div>
            </div>
          </div>
          {/* Informasjonsteknologi og medieproduksjon Vg1 */}
          <div className="timeline-item">
            <div className="timeline-dot alt" />
            <div className="timeline-card">
              <div className="timeline-title">
                {lang === "nb"
                  ? "Informasjonsteknologi og medieproduksjon Vg1"
                  : "Information Technology and Media Production Vg1"}
              </div>
              <div className="timeline-org">
                Kristiansund Videregående Skole
              </div>
              <div className="timeline-date">
                {lang === "nb"
                  ? "aug. 2021 – jun. 2022"
                  : "Aug 2021 – Jun 2022"}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default App;
